import { alpha, Box, Divider, PopoverOrigin, Stack } from '@mui/material'
import { useRef } from 'react'
import { RxChevronDown } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import { useAnchorRef } from '~/common/hooks/useAnchorRef'
import { Button } from '~/components/ui/atoms/button/Button'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { MenuItem } from '../../atoms/menu-item/MenuItem'
import { MenuItemOption } from '../../atoms/menu-item/types'
import { Menu } from '../../atoms/menu/Menu'
import { MenuButtonSize, MenuButtonVariant } from './types'
import { leftButtonPrimitives, rightButtonPrimitives } from './variants/primitives'

const useStyles = tss.create(({ theme }) => {
  return {
    buttons: {},
    menu: {
      '& .MuiPaper-root': {
        borderRadius: 1,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
          'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
          padding: '0',
        },
        '& .MuiMenuItem-root': {
          paddingBlock: '12px',
          paddingInline: '12px',
          marginBlock: '0',
          marginInline: '0',
          '&:active': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.selectedOpacity,
            ),
          },
        },
      },
    },
    divider: {
      '&.MuiDivider-root': {
        margin: '0px',
      },
    },
  }
})

export interface MenuButtonProps {
  align?: 'above' | 'below'
  variant: MenuButtonVariant
  size: MenuButtonSize
  text?: string
  options: MenuItemOption[]
  onLeftButtonClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  disabled?: boolean
  classes?: Partial<ReturnType<typeof useStyles>['classes']>
  startIcon?: React.ReactNode
  showChevron: boolean
  chevronIcon?: React.ReactNode
}

/**
 * Renders button group component with a left button and a right button that opens a dropdown menu
 */
export function MenuButton({
  align = 'below',
  variant,
  size,
  text,
  disabled = false,
  onLeftButtonClick,
  options,
  classes,
  startIcon,
  showChevron,
  chevronIcon,
}: MenuButtonProps) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const { isOpen, close, open, anchorEl } = useAnchorRef({ anchorRef })

  const { css, classes: classNames } = useStyles({ classesOverrides: classes })

  const iconSize = size === 'large' ? 16 : 12

  let anchorOrigin: PopoverOrigin
  let transformOrigin: PopoverOrigin
  if (align === 'above') {
    // If top align, set the anchor origin's bottom to the anchor element's top
    anchorOrigin = {
      vertical: 'top',
      horizontal: 'right',
    }
    transformOrigin = {
      vertical: 'bottom',
      horizontal: 'right',
    }
  } else {
    // If right align, set the anchor origin's left (with padding) to the anchor element's right
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'right',
    }
    transformOrigin = {
      vertical: 'top',
      horizontal: 'right',
    }
  }

  return (
    <Stack direction="row" spacing={0.125}>
      {text && (
        <Box ref={!showChevron ? anchorRef : undefined}>
          <Button
            classes={{
              button: css(leftButtonPrimitives, classNames.buttons),
            }}
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault()
              if (onLeftButtonClick) {
                onLeftButtonClick(event)
              } else {
                open()
              }
            }}
            size={size}
            startIcon={startIcon}
            variant={variant}
          >
            {text}
          </Button>
        </Box>
      )}
      {options.length > 0 && showChevron && (
        <Box ref={anchorRef}>
          <IconButton
            classes={{
              button: css(rightButtonPrimitives),
            }}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
              open()
            }}
            size={size}
            variant={variant}
          >
            {chevronIcon ? chevronIcon : <RxChevronDown size={iconSize} />}
          </IconButton>
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        className={classNames.menu}
        disableScrollLock
        onClick={close}
        onClose={close}
        open={isOpen}
        transformOrigin={transformOrigin}
      >
        {options.map((option, index) => (
          <div key={index}>
            <MenuItem disableRipple showCheckmark={false} {...option} variant="default" />
            {option.showDividerAfter && <Divider className={classNames.divider} />}
          </div>
        ))}
      </Menu>
    </Stack>
  )
}
